import type { UploadedFile } from '@/shared/components/form-fields/types';
import { getApiErrorMessage } from '@/shared/utils/parse-api-error-message.util';
import { BaseService } from '../base';

const PORTAL_API_URL = import.meta.env.VITE_API_URL as string | undefined;

interface PresignedUploadResponse {
  uploadUrl: string;
  key: string;
  bucket: string;
  expiresIn: number;
}

interface PresignedDownloadResponse {
  downloadUrl: string;
  expiresIn: number;
}

type UploadInput = File | UploadedFile;

function normalizeUploadInput(input: UploadInput): File {
  return input instanceof File ? input : input.file;
}

export class S3Service extends BaseService {
  constructor() {
    super();
  }

  /**
   * Upload via URL pré-assinada gerada pelo Portal.Api (sem credenciais S3 no browser).
   * @param prefix - Prefixo permitido pelo backend (ex.: inspections/follow-up/uuid)
   */
  async uploadFile(fileInput: UploadInput, prefix: string): Promise<string> {
    if (!PORTAL_API_URL?.trim()) {
      throw new Error(
        'VITE_API_URL é obrigatório para upload de arquivos (presigned URL).',
      );
    }

    const file = normalizeUploadInput(fileInput);
    const contentType = file.type || 'application/octet-stream';

    const response = await this.post(
      '/portal/v1/storage/presigned-upload',
      {
        prefix: prefix.trim(),
        fileName: file.name,
        contentType,
      },
      {},
      PORTAL_API_URL
    );

    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      if (!response.ok) {
        throw new Error(
          `Falha ao obter URL de upload (${response.status}): resposta inválida`,
        );
      }
      throw new Error('Resposta inválida do servidor ao solicitar upload.');
    }

    if (!response.ok) {
      throw new Error(
        getApiErrorMessage(
          payload,
          `Falha ao obter URL de upload (${response.status})`,
        ),
      );
    }

    const data = payload as PresignedUploadResponse;
    if (!data.uploadUrl || !data.key) {
      throw new Error('Resposta inválida do servidor ao solicitar upload.');
    }

    const putResponse = await fetch(data.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType },
    });

    if (!putResponse.ok) {
      throw new Error(
        `Falha ao enviar arquivo para o armazenamento (${putResponse.status}).`,
      );
    }

    return data.key;
  }

  async uploadFiles(files: UploadInput[], prefix: string): Promise<string[]> {
    if (!files.length) {
      return [];
    }

    return Promise.all(files.map(file => this.uploadFile(file, prefix)));
  }

  /**
   * URL GET pré-assinada via Portal.Api (IAM da API), alinhado aos PDFs de cotação/proposta.
   */
  async fetchPresignedDownloadUrl(key: string): Promise<string> {
    const trimmed = key?.trim();
    if (!trimmed) {
      throw new Error('Key do arquivo é obrigatória para download.');
    }
    if (!PORTAL_API_URL?.trim()) {
      throw new Error(
        'VITE_API_URL é obrigatório para download de arquivos (presigned URL).',
      );
    }

    const response = await this.post(
      '/portal/v1/storage/presigned-download',
      { key: trimmed },
      {},
      PORTAL_API_URL
    );

    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      if (!response.ok) {
        throw new Error(
          `Falha ao obter URL de download (${response.status}): resposta inválida`,
        );
      }
      throw new Error('Resposta inválida do servidor ao solicitar download.');
    }

    if (!response.ok) {
      throw new Error(
        getApiErrorMessage(
          payload,
          `Falha ao obter URL de download (${response.status})`,
        ),
      );
    }

    const data = payload as PresignedDownloadResponse;
    if (!data.downloadUrl) {
      throw new Error('Resposta inválida do servidor ao solicitar download.');
    }

    return data.downloadUrl;
  }
}

export const s3Service = new S3Service();
